import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendPasswordResetEmail, getAuth } from 'firebase/auth';
import { app } from '../firebase';
import MessageBox from '../components/MessageBox';
import { SNIFFSPOT_LOGO_URL, LOGIN_BACKGROUND_URL } from '../utils/constants';

function LoginPage({ onLoginSuccess }) {
    const { register, login, currentUser, userDetails, authErrorMessage, clearAuthError } = useAuth();
    const [view, setView] = useState('login'); 
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [newDisplayName, setNewDisplayName] = useState(''); 
    const [color, setColor] = useState('#60a5fa');
    const [homeAddress, setHomeAddress] = useState('');
    const [emergencyContactName, setEmergencyContactName] = useState('');
    const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
    const [cellPhoneNumber, setCellPhoneNumber] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const auth = getAuth(app);

    useEffect(() => {
        if (currentUser && !currentUser.isAnonymous && userDetails && userDetails.isActive !== false) {
            onLoginSuccess();
        }
    }, [currentUser, userDetails, onLoginSuccess]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setMessage('');
        clearAuthError();
        try {
            await login(email, password);
        } catch (error) {
            // Handled by context
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setMessage('');
        clearAuthError();
        try {
            await register(email, password, firstName, lastName, color, homeAddress, emergencyContactName, emergencyContactPhone, cellPhoneNumber, newDisplayName);
            setMessage('Registration successful! You are now logged in.');
            setMessageType('info');
        } catch (error) {
            if (error.code === 'auth/email-already-in-use') {
                setMessage('This email address is already in use. Please try logging in instead.');
            } else {
                setMessage(`Registration failed: ${error.message}`);
            }
            setMessageType('error');
        }
    };
    
    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setMessage('');
        clearAuthError();
        if (!email) {
            setMessage('Please enter your email address.');
            setMessageType('error');
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            setMessage('Password reset email sent. Please check your inbox.');
        } catch (error) {
            setMessage(error.message);
            setMessageType('error');
        }
    };

    return (
        <div
            className="relative flex items-center justify-center min-h-screen bg-cover bg-center p-4"
            style={{ backgroundImage: `url(${LOGIN_BACKGROUND_URL})` }}
        >
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            <div className="relative bg-white p-8 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 z-10">
                
                {/* NEW: Website Link Button */}
                <Link to="/" className="absolute top-4 left-4 text-sm font-bold text-gray-400 hover:text-blue-600 transition duration-200">
                    ← Website
                </Link>

                <img
                    src={SNIFFSPOT_LOGO_URL}
                    alt="Shep's Sniff Spot Logo"
                    className="mx-auto w-48 h-auto mb-6 mt-4"
                    onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/192x64/000/FFF?text=Sniffspot"; }}
                />
                
                {view === 'login' && (
                    <>
                        <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-6">Login to Shep's Sniff Spot</h2>
                        <form onSubmit={handleLogin} className="space-y-5">
                            <div>
                                <label htmlFor="email-login" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" id="email-login" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="your@email.com" />
                            </div>
                            <div>
                                <label htmlFor="password-login" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                <input type="password" id="password-login" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="********" />
                            </div>
                            <button type="submit" className="w-full py-3 px-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">Login</button>
                        </form>
                        <div className="mt-6 text-center text-sm">
                            <button onClick={() => setView('forgotPassword')} className="text-blue-600 hover:text-blue-800">Forgot Password?</button>
                        </div>
                        <div className="mt-2 text-center">
                            <button onClick={() => setView('register')} className="text-blue-600 hover:text-blue-800">Need an account? Register</button>
                        </div>
                    </>
                )}

                {view === 'register' && (
                        <>
                        <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-6">Register for Sniffspot</h2>
                        <form onSubmit={handleRegister} className="space-y-5">
                            {/* ... Fields here (Email, Pass, DisplayName, First, Last, Color, Address, Emergency Contact, Cell) ... */}
                            <div>
                                <label htmlFor="email-register" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" id="email-register" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="your@email.com" />
                            </div>
                            <div>
                                <label htmlFor="password-register" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                <input type="password" id="password-register" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="********" />
                            </div>
                            <div>
                                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">Display Name (for Bookings)</label>
                                <input type="text" id="displayName" value={newDisplayName} onChange={(e) => setNewDisplayName(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Dog Lover 77" />
                            </div>
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input type="text" id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="Your First Name" />
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Surname</label>
                                <input type="text" id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="Your Surname" />
                            </div>
                            <div>
                                <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">Choose Your Booking Color</label>
                                <input type="color" id="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-full h-10 rounded-lg border border-gray-300" />
                            </div>
                            <div>
                                <label htmlFor="homeAddress" className="block text-sm font-medium text-gray-700 mb-1">Home Address</label>
                                <input type="text" id="homeAddress" value={homeAddress} onChange={(e) => setHomeAddress(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="123 Dog Park Lane" />
                            </div>
                            <div>
                                <label htmlFor="emergencyContactName" className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Name</label>
                                <input type="text" id="emergencyContactName" value={emergencyContactName} onChange={(e) => setEmergencyContactName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-border-blue-500" placeholder="John Doe" />
                            </div>
                            <div>
                                <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Phone</label>
                                <input type="tel" id="emergencyContactPhone" value={emergencyContactPhone} onChange={(e) => setEmergencyContactPhone(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-border-blue-500" placeholder="081-123-4567" />
                            </div>
                            <div>
                                <label htmlFor="cellPhoneNumber" className="block text-sm font-medium text-gray-700 mb-1">Cell Phone Number</label>
                                <input type="tel" id="cellPhoneNumber" value={cellPhoneNumber} onChange={(e) => setCellPhoneNumber(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="555-987-6543" />
                            </div>
                            <button type="submit" className="w-full py-3 px-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">Register</button>
                        </form>
                        <div className="mt-6 text-center">
                            <button onClick={() => setView('login')} className="text-blue-600 hover:text-blue-800">Already have an account? Login</button>
                        </div>
                    </>
                )}

                {view === 'forgotPassword' && (
                    <>
                        <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-6">Reset Password</h2>
                        <form onSubmit={handlePasswordReset} className="space-y-5">
                            <div>
                                <label htmlFor="email-reset" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" id="email-reset" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="your@email.com" />
                            </div>
                            <button type="submit" className="w-full py-3 px-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">Send Reset Email</button>
                        </form>
                        <div className="mt-6 text-center">
                            <button onClick={() => setView('login')} className="text-blue-600 hover:text-blue-800">Back to Login</button>
                        </div>
                    </>
                )}

            </div>
            <MessageBox message={authErrorMessage || message} type={authErrorMessage ? 'error' : messageType} onClose={() => { clearAuthError(); setMessage(''); }} />
        </div>
    );
}

export default LoginPage;